// src/api.js
import { decodeJwt } from './utilities'; // assuming you have it

const API_CONFIG = {
  dev: import.meta.env.VITE_API_BASE_URL || '',
  stage: import.meta.env.VITE_API_BASE_URL || '',
  prod: import.meta.env.VITE_API_BASE_URL || '',
};

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'dev' ;
const REG_CODE = import.meta.env.VITE_REG_CODE || 'REDIMDWM';
const IS_PROD = ENVIRONMENT === 'prod';
export const BASE_URL = API_CONFIG[ENVIRONMENT];

const VIDEO_API_CONFIG = {
  dev: import.meta.env.VITE_VIDEO_API_BASE_URL || import.meta.env.VITE_API_PROXY_TARGET || BASE_URL,
  stage: import.meta.env.VITE_VIDEO_API_BASE_URL || 'https://clients.stage.redimd.com',
  prod: import.meta.env.VITE_VIDEO_API_BASE_URL || 'https://clients.redimd.com',
};

const REDIMD_WWW_CONFIG = {
  // localhost literal only exists in dev-mode builds (MODE is replaced at build time)
  dev: import.meta.env.VITE_REDIMD_WWW_URL || (import.meta.env.MODE === 'dev' ? 'http://localhost:3000' : 'https://redimd.com'),
  stage: import.meta.env.VITE_REDIMD_WWW_URL || 'https://stage.redimd.com',
  prod: import.meta.env.VITE_REDIMD_WWW_URL || 'https://redimd.com',
};

export const SECURITY_URL = `${(REDIMD_WWW_CONFIG[ENVIRONMENT] || REDIMD_WWW_CONFIG.prod).replace(/\/+$/, '')}/security`;

const VIDEO_API_BASE_URL = VIDEO_API_CONFIG[ENVIRONMENT] || BASE_URL || '';

export const buildVideoConferenceUrl = (opentokToken) => {
  if (!opentokToken) return '';
  const baseUrl = VIDEO_API_BASE_URL.replace(/\/+$/, '');
  return `${baseUrl}/api/video/${encodeURIComponent(opentokToken)}`;
};

const NETWORK_ERROR_MESSAGE = 'Unable to reach RediMD. Check your connection and try again.';

const getRequestErrorMessage = (error) => {
  const message = error?.message || '';
  return message === 'Failed to fetch' ? NETWORK_ERROR_MESSAGE : message || 'Network error or server request failure.';
};

const parseJsonResponse = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const isRegistrationOnlyJwt = (jwt) => {
  const claims = decodeJwt(jwt);
  return Boolean(
    claims
    && claims.token_use === 'registration'
    && ['contact_otp', 'email_otp'].includes(claims.auth_level)
    && claims.scope === 'registration:continue'
  );
};

export async function fetchWithJwt(url, options = {}, autoLogout = true) {
  const jwt = localStorage.getItem('redimdwm_jwt');
  const headers = {
    'Content-Type': 'application/json',
    ...(jwt ? { 'Authorization': `Bearer ${jwt}` } : {}),
    ...(options.headers || {})
  };

  const config = {
    method: options.method || 'GET',
    headers,
    ...options,
    ...(options.method?.toUpperCase() !== 'GET' && {
      body: typeof options.body === 'string'
        ? options.body
        : options.body
          ? JSON.stringify(options.body)
          : '{}'
    })
  };

  try {
    const response = await fetch(url, config);
    const data = await parseJsonResponse(response);
  
    // ❌ Expired or invalid token — auto cleanup
    if (response.status === 401 || data?.error === 'Unauthorized') {
      localStorage.removeItem('redimdwm_jwt');

      // Optionally: trigger logout flow or redirect
      if (autoLogout && typeof window?.logoutUser === 'function') {
        window.logoutUser(true);
      }

      // Optionally return custom signal for higher-level error handling
      return { error: data.error || 'Session expired. Please log in again.', expired: true };
    }

    // 🧠 Handle JWT update and reset the sessionTimeOut timer
    if (data.jwt) {
      localStorage.setItem('redimdwm_jwt', data.jwt);
      const decoded = decodeJwt(data.jwt);
      if (decoded && decoded.exp && !isRegistrationOnlyJwt(data.jwt)) {
        const timeoutSeconds = decoded.exp - Math.floor(Date.now() / 1000);
        if (window.startSessionTimer) { 
          window.startSessionTimer(timeoutSeconds); // Restart session timer globally
        }
      }  
    }

    if (!response.ok) {
      // Preserve the full server response so callers can branch on structured
      // errors (e.g. 422 `contact_required` carries `preferred_contact`,
      // `email_ok`, `phone_ok` for the UI to route the user to the right fix
      // screen). Force a non-empty `error` field so the existing
      // `if (response.error)` failure-check pattern still trips.
      return {
        ...data,
        error: data.error || data.errorMsg || `Request failed: ${response.statusText}`,
        httpStatus: response.status,
      };
    }

    return data;
  } catch (error) {
    return { error: getRequestErrorMessage(error) };
  }
}

export async function fetchPublicJson(url, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  const config = {
    method: options.method || 'GET',
    headers,
    ...options,
    ...(options.method?.toUpperCase() !== 'GET' && {
      body: typeof options.body === 'string'
        ? options.body
        : options.body
          ? JSON.stringify(options.body)
          : '{}'
    })
  };

  try {
    const response = await fetch(url, config);
    const data = await parseJsonResponse(response);

    if (!response.ok) {
      const errMsg = data.error || data.errorMsg || `Request failed: ${response.statusText}`;
      throw new Error(errMsg);
    }

    return data;
  } catch (error) {
    return { error: getRequestErrorMessage(error) };
  }
}

async function fetchWithActionJwt(url, actionJwt, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
    'Authorization': `Action ${actionJwt}`,
  };

  const config = {
    method: options.method || 'GET',
    ...options,
    headers,
    ...(options.method?.toUpperCase() !== 'GET' && {
      body: typeof options.body === 'string'
        ? options.body
        : options.body
          ? JSON.stringify(options.body)
          : '{}'
    })
  };

  try {
    const response = await fetch(url, config);
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      return {
        ...data,
        error: data.error || data.errorMsg || 'Unable to continue this payment.',
        httpStatus: response.status,
      };
    }
    return data;
  } catch (error) {
    return { error: getRequestErrorMessage(error) };
  }
}

// NOTE: the `Authorization: Finalize <jwt>` transport was removed with FX-179.
// It existed so the browser could report a capture IT had performed, using a
// one-shot token minted by remote_authorize_payment_capture. The server now
// performs the capture itself, so nothing mints or carries that token any
// more. The backend still accepts the header; wm no longer sends it.

export const api = {
  //=========================================================================
  //  security APIs
  //=========================================================================
  async checkJwt() {
    return fetchWithJwt(`${BASE_URL}/auth/check_session`, {
      method: 'GET',
    },false);
  },

  async login(data) {
    localStorage.removeItem('redimdwm_jwt');
    return fetchWithJwt(`${BASE_URL}/auth/remote_login`, {
      method: 'POST',
        body: data
      },false);
  },

  async getPasskeyLoginOptions(data = {}) {
    localStorage.removeItem('redimdwm_jwt');
    return fetchWithJwt(`${BASE_URL}/auth/remote_passkey_login_options`, {
      method: 'POST',
      body: data
    }, false);
  },

  async verifyPasskeyLogin(data = {}) {
    localStorage.removeItem('redimdwm_jwt');
    return fetchWithJwt(`${BASE_URL}/auth/remote_passkey_login_verify`, {
      method: 'POST',
      body: data
    }, false);
  },

  async getPasskeyRegisterOptions(data = {}) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_passkey_register_options`, {
      method: 'POST',
      body: data
    });
  },

  async verifyPasskeyRegister(data = {}) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_passkey_register_verify`, {
      method: 'POST',
      body: data
    });
  },

  async listPasskeys() {
    return fetchWithJwt(`${BASE_URL}/auth/remote_passkey_list`, {
      method: 'GET',
    });
  },

  async deletePasskey(data = {}) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_passkey_delete`, {
      method: 'POST',
      body: data
    });
  },

  async register(data) {
    localStorage.removeItem('redimdwm_jwt');
    data.code = REG_CODE;
    return fetchWithJwt(`${BASE_URL}/auth/remote_register`, {
      method: 'POST',
        body: data
      });
  },

  async verifyOTP({ capability, otp } = {}) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_verify_otp`, {
      method: 'POST',
        body: { capability, otp }
      }, false);
  },

  async resendOTP({ capability, origin } = {}) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_resend_otp`, {
      method: 'POST',
        body: { capability, origin }
      }, false);
  },

  async forgotPassword(data) {
    const loginName = String(data?.user_name || data?.identifier || data?.email || '').trim();
    localStorage.removeItem('redimdwm_jwt');
    return fetchWithJwt(`${BASE_URL}/auth/remote_forgot_password`, {
      method: 'POST',
        body: {
          ...data,
          user_name: loginName,
          identifier: data?.identifier || loginName,
          email: data?.email || loginName,
        }
      }, false);
  },

  async resetPassword(data) {
    localStorage.removeItem('redimdwm_jwt');
    return fetchWithJwt(`${BASE_URL}/auth/remote_reset_password`, {
      method: 'POST',
        body: data
      }, false);
  },

  async completeResetPassword(data) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_complete_reset`, {
      method: 'POST',
        body: data
      });
  },

  async changePassword(data) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_change_password`, {
      method: 'POST',
        body: data
      });
  },

  //=========================================================================
  //  account recovery APIs (duplicate-registration hard-reject flow, #203)
  //=========================================================================
  async recoveryOptions(data) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_recovery_options`, {
      method: 'POST',
        body: data
      }, false);
  },

  async recoveryProveContact(data) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_recovery_prove_contact`, {
      method: 'POST',
        body: data
      }, false);
  },

  async recoveryVerifyCode(data) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_recovery_verify_code`, {
      method: 'POST',
        body: data
      }, false);
  },

  async recoverySetPassword(data) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_recovery_set_password`, {
      method: 'POST',
        body: data
      }, false);
  },

  //=========================================================================
  //  user data APIs
  //=========================================================================
  async getUserInfo() {
    return fetchWithJwt(`${BASE_URL}/user/remote_get_user_info`, {
      method: 'GET',
    },false);
  },


  async verifyAddress(data) {
    return fetchWithJwt(`${BASE_URL}/user/remote_address_verify`, {
      method: 'POST',
      body: data
    }, false);
  },

  async getRegistrationStep({ capability } = {}) {
    return fetchWithJwt(`${BASE_URL}/auth/remote_get_registration_step`, {
      method: 'POST',
      body: capability ? { capability } : {}
    }, false);
  },

  async exchangePaymentActionCapability({ capability } = {}) {
    return fetchPublicJson(`${BASE_URL}/auth/remote_exchange_action_capability`, {
      method: 'POST',
      body: { capability }
    });
  },

  async getHealthInfo() {
    return fetchWithJwt(`${BASE_URL}/user/remote_health_info`, {
      method: 'GET',
    });
  },

  async updateUserInfo(data) {
    return fetchWithJwt(`${BASE_URL}/user/remote_update_user_info`, {
      method: 'POST',
      body: data
    });
  },

  async updateHealthInfo(data) {
    return fetchWithJwt(`${BASE_URL}/user/remote_update_health_info`, {
      method: 'POST',
      body: data
    });
  },

  async cancelAccount() {
    return fetchWithJwt(`${BASE_URL}/user/remote_cancel_account`, {
      method: 'GET'
    });
  },

  //=========================================================================
  //  order mgmt APIs
  //=========================================================================
  async getPriceInfo() {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_get_pricing_info`, {
      method: 'GET'
    });
  },

  async getPublicPriceInfo() {
    return fetchPublicJson(`${BASE_URL}/cp/orders/remote_get_public_pricing_info`, {
      method: 'GET'
    });
  },
  
  async getOrderDetail(data = {}) {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_get_order`, {
      method: 'POST',
      body: data
    });
  },

  async getOrderDetailWithAction(actionJwt, orderId) {
    return fetchWithActionJwt(`${BASE_URL}/cp/orders/remote_get_order`, actionJwt, {
      method: 'POST',
      body: { orderid: orderId }
    });
  },

  async getOrders(data = {}) {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_get_orders`, {
      method: 'POST',
      body: data
    });
  },
  
  async getOrderHistory() {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_order_history`, {
      method: 'GET',
    });
  },

  async getOrdersDetail(data = {}) {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_get_orders_detailed`, {
      method: 'POST',
      body: data
    });
  },

  async addOrder(data = {}) {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_add_order`, {
      method: 'POST',
      body: data
    });
  },

  async applyPayment(data) {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_apply_payment`, {
      method: 'POST',
      body: data
    });
  },

  /**
   * PAY-2 - ask the server to open one payment for one RediMD order.
   *
   * Request:  { orderid }
   * Response: { success: true, provider: "paypal", handle, surcharge_cents, total_cents }
   *         | { success: true, provider: "bank", config: { capture_context }, surcharge_cents, total_cents }
   *         | { success: false, message }
   *
   * The order number and the authenticated identity are the whole request.
   * The server owns the amount, the reservation and the choice of provider.
   */
  async createPayment(data = {}) {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_create_payment`, {
      method: 'POST',
      body: data
    }, false);
  },

  async createPaymentWithAction(actionJwt, data = {}) {
    return fetchWithActionJwt(`${BASE_URL}/cp/orders/remote_create_payment`, actionJwt, {
      method: 'POST',
      body: data
    });
  },

  /**
   * PAY-2 - capture, record and receipt one RediMD order, SERVER-SIDE.
   *
   * Request:  { orderid, provider: "paypal", handle }
   *         | { orderid, provider: "bank", transient_token }
   * Response: { success: true, message, ... } | { success: false, message }
   *
   * The capture, the payment row, the order status and the invoice are all
   * done inside this one request, so the caller must NOT apply the payment
   * again afterwards. Callers show `message` verbatim and must not infer from
   * the HTTP status whether money moved - only the server knows.
   */
  async capturePayment(data = {}) {
    return fetchWithJwt(`${BASE_URL}/cp/orders/remote_capture_payment`, {
      method: 'POST',
      body: data
    }, false);
  },

  async capturePaymentWithAction(actionJwt, data = {}) {
    return fetchWithActionJwt(`${BASE_URL}/cp/orders/remote_capture_payment`, actionJwt, {
      method: 'POST',
      body: data
    });
  },

  async applyPaymentWithAction(actionJwt, data) {
    return fetchWithActionJwt(`${BASE_URL}/cp/orders/remote_apply_payment`, actionJwt, {
      method: 'POST',
      body: data
    });
  },

  //=========================================================================
  //  appointment and schedule mgmt APIs
  //=========================================================================
  async getAppts(data = {}) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_get_appointments`, {
      method: 'POST',
      body: data
    });
  },

  async saveMedHistory(data) {
    return fetchWithJwt(`${BASE_URL}/user/remote_save_medical_history`, {
      method: 'POST',
      body: data
    });
  },

  async getAvailableDoctors(data) {
    return fetchWithJwt(`${BASE_URL}/appointment/ajax_get_available_doctors_in_plan`, {
      method: 'POST',
      body: data
    });
  },

  async getAvailableDays(data) {
    return fetchWithJwt(`${BASE_URL}/appointment/ajax_get_available_days`, {
      method: 'POST',
      body: data
    });
  },

  async getAvailableTimes(data) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_get_all_times`, {
      method: 'POST',
      body: data
    });
  },

  async chatBot(data) {
    return fetchWithJwt(`${BASE_URL}/api/rdmdwmChatBot`, {
      method: 'POST',
      body: data
    });
  },

  async bookAppointment(data) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_book_appointment`, {
      method: 'POST',
      body: data
    });
  },

  /**
   * Reserve an appointment slot (pending_payment state, 15-min TTL).
   * Call BEFORE opening the PayPal popup. On success, store appointment_id
   * and expires_at, then proceed to payment.
   * @param {Object} data - { apptdate, doctor_user_id, intake, origin }
   * @returns {Promise} - { success, appointment_id, expires_at } or error
   */
  async reserveAppointmentPending(data = {}) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_reserve_appointment_pending`, {
      method: 'POST',
      body: data
    });
  },

  /**
   * PAY-2 - ask the server to open one payment for one pending appointment copay.
   *
   * Request:  { appointment_id }
   * Response: { success: true, provider: "paypal", handle, surcharge_cents, total_cents }
   *         | { success: true, provider: "bank", config: { capture_context }, surcharge_cents, total_cents }
   *         | { success: false, message }
   *
   * The server owns the copay amount and the choice of provider.
   */
  async createCopayPayment(data = {}) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_create_payment`, {
      method: 'POST',
      body: data
    }, false);
  },

  /**
   * PAY-2 - capture, record and book one appointment copay, SERVER-SIDE.
   *
   * Request:  { appointment_id, provider: "paypal", handle }
   *         | { appointment_id, provider: "bank", transient_token }
   * Response: { success: true, message, url, appointment_id, jwt }
   *         | { success: false, message, retry }
   *
   * The server books the appointment inside this call; there is no finalize step.
   */
  async captureCopayPayment(data = {}) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_capture_payment`, {
      method: 'POST',
      body: data
    }, false);
  },

  /**
   * Cancel a pending appointment (idempotent — no-op if already gone/finalized).
   * Use for cleanup when the user navigates away before completing payment.
   * @param {Object} data - { appointment_id }
   * @returns {Promise} - { success }
   */
  async cancelPendingAppointment(data = {}) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_cancel_pending`, {
      method: 'POST',
      body: data
    });
  },

  /**
   * Fire-and-forget cancel for unload / unmount paths.
   * navigator.sendBeacon() cannot set custom headers, so we fall back to
   * fetch({ keepalive: true }) which survives page unload on modern browsers
   * and still carries the Authorization header. The cancel endpoint is
   * idempotent, so silent failure is acceptable.
   * @param {number} appointment_id
   */
  beaconCancelPendingAppointment(appointment_id) {
    if (!appointment_id) return;
    const url = `${BASE_URL}/appointment/remote_cancel_pending`;
    const jwt = localStorage.getItem('redimdwm_jwt');
    const headers = {
      'Content-Type': 'application/json',
      ...(jwt ? { 'Authorization': `Bearer ${jwt}` } : {})
    };
    try {
      // keepalive: true allows the request to outlive the page unload event.
      // sendBeacon is NOT used here because it cannot carry the Authorization
      // header. keepalive fetch is equivalent for our purposes.
      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ appointment_id }),
        keepalive: true
      });
    } catch {
      // Intentional no-op — fire-and-forget; TTL self-heals the slot.
    }
  },

  async attendAppointment(data) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_goto_appt`, {
      method: 'POST',
      body: data
    });
  },

  async cancelAppointment(data) {
    return fetchWithJwt(`${BASE_URL}/appointment/remote_cancel_appointment`, {
      method: 'POST',
      body: data
    });
  },

};
